import { atom } from 'jotai';
import type { Session, User } from '@supabase/supabase-js';

import type { Permission } from '@/lib/permissions';

export interface AuthMeUser {
  id: string;
  email: string | null;
  name: string | null;
  avatar_url: string | null;
  username: string | null;
  bio: string | null;
  website: string | null;
  phone: string | null;
  location: string | null;
  language: 'es' | 'en' | 'pt';
  timezone: string | null;
  social_x: string | null;
  social_instagram: string | null;
  social_linkedin: string | null;
  social_facebook: string | null;
  social_github: string | null;
  social_youtube: string | null;
}

export interface AuthMeResponse {
  user: AuthMeUser;
  organizations: {
    id: string;
    name: string;
    slug: string;
    /** R2 key or absolute URL (`organizations.logo_url`). */
    logo_url: string | null;
    primary_color: string | null;
    /** The account's own space: cannot be deleted on its own. Optional for older API responses. */
    is_personal?: boolean;
    /** Plan name (`plans.name`) of the org; null = none assigned (counts as Basic). Optional for older API responses. */
    plan?: string | null;
    role: 'owner' | 'admin' | 'editor' | 'viewer';
    /** Effective permissions of the member in this org (catalog order). Optional for older API responses; see `lib/permissions.ts`. */
    permissions?: Permission[];
    /**
     * `false` = an editor/viewer limited to `project_ids` (owner/admin are always
     * `true`). Optional only so a response from an older API reads as "all".
     */
    all_projects?: boolean;
    project_ids?: string[];
    /** When the user joined; `invited` = through an invitation (drives the "Nuevo" badge). */
    joined_at?: string | null;
    invited?: boolean;
  }[];
  active_organization_id: string | null;
  /** Pending invitations addressed to the user (drives the bell's count). Optional for older API responses. */
  pending_invitations?: number;
  /** Unread rows of the user's notification history (drives the bell's count). Optional for older API responses. */
  unread_notifications?: number;
}

/**
 * Session state. Three values:
 *   - `undefined` → still hydrating from Supabase localStorage on app boot
 *   - `null`      → hydrated, user is not logged in
 *   - `Session`   → hydrated, user is logged in
 *
 * `ProtectedRoute` uses the `undefined` state to render a loader instead of
 * redirecting to /login. Without this, a hard refresh raced the redirect
 * past the rehydrate and kicked the user out.
 */
export const sessionAtom = atom<Session | null | undefined>(undefined);
export const supabaseUserAtom = atom<User | null>(null);
export const meAtom = atom<AuthMeResponse | null>(null);
export const activeOrgIdAtom = atom<string | null>(null);

/**
 * Where `/auth/me` stands. Derived, so anything that fills `meAtom` is `ready`:
 *   - `loading` → no account yet (the request is on its way, or retrying)
 *   - `ready`   → `meAtom` holds the account (a later refetch error keeps it)
 *   - `failed`  → the request failed and nothing is loading it any more
 * Pages and the sidebar use it to tell "not loaded yet" (skeleton) from "this
 * really does not exist" (not found, "Nueva organización").
 */
export type MeStatus = 'loading' | 'ready' | 'failed';
/** Set by `App` from the `/auth/me` query: true while it has failed and is not retrying. */
export const meFailedAtom = atom(false);
/** Set by `App`: asks `/auth/me` again. Wrapped in an object because atoms treat bare functions as updaters. */
export const meRetryAtom = atom<{ run: () => void } | null>(null);
export const meStatusAtom = atom<MeStatus>(get => (get(meAtom) ? 'ready' : get(meFailedAtom) ? 'failed' : 'loading'));

export const activeOrgAtom = atom(get => {
  const me = get(meAtom);
  const id = get(activeOrgIdAtom);
  if (!me || !id) return null;
  return me.organizations.find(o => o.id === id) ?? null;
});
